import { useState, useMemo } from "react";
import { Search, Filter, Clock, User, Stethoscope, Building2, MapPin } from "lucide-react";
import { Card } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { ScrollArea } from "../../../components/ui/scroll-area";
import type { Appointment, Doctor, Department } from "../../../shared/schema";

interface AppointmentTableProps {
  appointments: Appointment[];
  doctors?: Doctor[];
  departments?: Department[];
  showDepartment?: boolean;
  showDoctor?: boolean;
  title?: string;
  emptyMessage?: string;
}

const statusConfig = {
  scheduled: { label: "Đã đặt", variant: "secondary" as const },
  confirmed: { label: "Đã xác nhận", variant: "default" as const },
  completed: { label: "Hoàn thành", variant: "outline" as const },
  cancelled: { label: "Đã hủy", variant: "destructive" as const },
};

export function AppointmentTable({
  appointments,
  doctors = [],
  departments = [],
  showDepartment = true,
  showDoctor = true,
  title = "Danh sách lịch hẹn",
  emptyMessage = "Không có lịch hẹn nào",
}: AppointmentTableProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState<string>("all");
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredAppointments = useMemo(() => {
    return appointments.filter(apt => {
      const matchesSearch = searchQuery === "" || 
        apt.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        apt.doctorName.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesDoctor = selectedDoctor === "all" || apt.doctorId === selectedDoctor;
      const matchesDepartment = selectedDepartment === "all" || apt.departmentId === selectedDepartment;
      
      return matchesSearch && matchesDoctor && matchesDepartment;
    });
  }, [appointments, searchQuery, selectedDoctor, selectedDepartment]);

  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  const paginatedAppointments = filteredAppointments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const uniqueDoctors = useMemo(() => {
    if (doctors.length > 0) return doctors;
    const seen = new Map<string, { id: string; name: string }>();
    appointments.forEach(apt => {
      if (!seen.has(apt.doctorId)) {
        seen.set(apt.doctorId, { id: apt.doctorId, name: apt.doctorName });
      }
    });
    return Array.from(seen.values());
  }, [appointments, doctors]);

  const uniqueDepartments = useMemo(() => {
    if (departments.length > 0) return departments;
    const seen = new Map<string, { id: string; name: string }>();
    appointments.forEach(apt => {
      if (!seen.has(apt.departmentId)) {
        seen.set(apt.departmentId, { id: apt.departmentId, name: apt.departmentName });
      }
    });
    return Array.from(seen.values());
  }, [appointments, departments]);

  return (
    <Card className="border border-card-border">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Tìm bệnh nhân, bác sĩ..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-9 w-[200px]"
                data-testid="input-search-appointments"
              />
            </div>
            
            {showDoctor && uniqueDoctors.length > 1 && (
              <Select 
                value={selectedDoctor} 
                onValueChange={(val) => {
                  setSelectedDoctor(val);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[180px]" data-testid="select-filter-doctor">
                  <Stethoscope className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Bác sĩ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả bác sĩ</SelectItem>
                  {uniqueDoctors.map((doc) => (
                    <SelectItem key={doc.id} value={doc.id}>
                      {doc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {showDepartment && uniqueDepartments.length > 1 && (
              <Select 
                value={selectedDepartment} 
                onValueChange={(val) => {
                  setSelectedDepartment(val);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[160px]" data-testid="select-filter-department">
                  <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Khoa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả khoa</SelectItem>
                  {uniqueDepartments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {'nameVi' in dept ? String(dept.nameVi) : dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      <ScrollArea className="h-[400px]">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10">
            <TableRow>
              <TableHead className="w-[80px]">
                <div className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  Giờ
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5" />
                  Bệnh nhân
                </div>
              </TableHead>
              {showDoctor && (
                <TableHead>
                  <div className="flex items-center gap-1">
                    <Stethoscope className="h-3.5 w-3.5" />
                    Bác sĩ
                  </div>
                </TableHead>
              )}
              {showDepartment && (
                <TableHead>
                  <div className="flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5" />
                    Khoa
                  </div>
                </TableHead>
              )}
              <TableHead>
                <div className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  Phòng
                </div>
              </TableHead>
              <TableHead className="text-right">Trạng thái</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedAppointments.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={showDoctor && showDepartment ? 6 : showDoctor || showDepartment ? 5 : 4} 
                  className="h-32 text-center"
                >
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Filter className="h-8 w-8 opacity-50" />
                    <p>{emptyMessage}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedAppointments.map((apt, index) => (
                <TableRow 
                  key={apt.id}
                  className="hover-elevate"
                  data-testid={`row-appointment-${apt.id}`}
                >
                  <TableCell className="font-medium text-foreground">
                    {apt.time}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-foreground">{apt.patientName}</span>
                      <span className="text-xs text-muted-foreground">{apt.specialtyName}</span>
                    </div>
                  </TableCell>
                  {showDoctor && (
                    <TableCell className="text-muted-foreground">
                      {apt.doctorName}
                    </TableCell>
                  )}
                  {showDepartment && (
                    <TableCell className="text-muted-foreground">
                      {apt.departmentName}
                    </TableCell>
                  )}
                  <TableCell className="text-muted-foreground">
                    {apt.room}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={statusConfig[apt.status].variant}>
                      {statusConfig[apt.status].label}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>

      {filteredAppointments.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-border">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Hiển thị</span>
            <Select 
              value={String(itemsPerPage)} 
              onValueChange={(val) => {
                setItemsPerPage(Number(val));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[70px]" data-testid="select-items-per-page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <span>trong {filteredAppointments.length} kết quả</span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="button-prev-page"
            >
              Trước
            </Button>
            <span className="text-sm text-muted-foreground px-2">
              Trang {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="button-next-page"
            >
              Sau
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
